import { onLiveTrackingHost } from "./live-tracking-host";

export const META_PIXEL_ID = "743072714970606";

const SCRIPT_SRC = "https://connect.facebook.net/en_US/fbevents.js";

type MetaPixelCall = readonly unknown[];

/** Shape of Meta's official stub; fbevents.js fills `callMethod`. */
export interface MetaPixelQueue {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  readonly queue: MetaPixelCall[];
  push?: MetaPixelQueue;
  readonly loaded: boolean;
  readonly version: string;
  /** We fire route PageViews ourselves; blocks fbevents' history auto-PageView. */
  readonly disablePushState: boolean;
  /** fbevents keeps one PageView per document otherwise; dedupe is `trackMetaPageView`. */
  readonly allowDuplicatePageViews: boolean;
}

declare global {
  interface Window {
    fbq?: MetaPixelQueue;
    _fbq?: MetaPixelQueue;
  }
}

/** Meta standard events of the demo funnel: content seen, form, booking page, thank-you. */
export type MetaFunnelEvent =
  | "ViewContent"
  | "SubmitApplication"
  | "Schedule"
  | "CompleteRegistration";

/** Advanced matching keys. fbevents lowercases and hashes them in the browser. */
export interface MetaUserData {
  readonly em: string;
  readonly ph?: string;
  readonly fn?: string;
  readonly ln?: string;
}

const pixel = {
  loaded: false,
  granted: false,
  lastPageViewPath: null as string | null,
  sentEvents: new Set<MetaFunnelEvent>(),
  waitingEvents: [] as MetaFunnelEvent[],
  userData: null as MetaUserData | null,
};

function flushWaitingEvents(): void {
  for (const name of pixel.waitingEvents.splice(0)) {
    window.fbq?.("track", name);
  }
}

function createQueue(): MetaPixelQueue {
  const calls: MetaPixelCall[] = [];
  const queue: MetaPixelQueue = Object.assign(
    (...args: unknown[]): void => {
      if (queue.callMethod) queue.callMethod.apply(queue, args);
      else calls.push(args);
    },
    {
      queue: calls,
      loaded: true,
      version: "2.0",
      disablePushState: true,
      allowDuplicatePageViews: true,
    },
  );
  queue.push = queue;
  return queue;
}

function insertScript(): void {
  const script = document.createElement("script");
  script.async = true;
  script.src = SCRIPT_SRC;
  document.head.appendChild(script);
}

/** Loads fbevents.js on first grant, live hosts only: localhost events stay in `fbq.queue`. Idempotent. */
export function grantMetaPixel(): void {
  if (typeof window === "undefined" || pixel.granted) return;
  if (pixel.loaded) {
    window.fbq?.("consent", "grant");
    pixel.granted = true;
    flushWaitingEvents();
    return;
  }
  const existing: MetaPixelQueue | undefined = window.fbq;
  const fbq: MetaPixelQueue = existing ?? createQueue();
  if (existing === undefined) {
    window.fbq = fbq;
    window._fbq = window._fbq ?? fbq;
    if (onLiveTrackingHost()) insertScript();
  }
  // Auto events would guess conversions from button clicks: only our funnel steps send them.
  fbq("set", "autoConfig", false, META_PIXEL_ID);
  fbq("init", META_PIXEL_ID, pixel.userData ?? undefined);
  pixel.loaded = true;
  pixel.granted = true;
  flushWaitingEvents();
}

/** Once per document: fbevents takes user data on re-init only while it has none. */
export function setMetaUserData(data: MetaUserData): void {
  if (typeof window === "undefined" || pixel.userData !== null) return;
  pixel.userData = data;
  // Automatic matching sent no contact data (Meta stats 10/2026): pass the form values explicitly.
  if (pixel.loaded) window.fbq?.("init", META_PIXEL_ID, data);
}

/** Holds every further event; cookies go via `deleteUnconsentedCookies`. */
export function revokeMetaPixel(): void {
  if (typeof window === "undefined") return;
  if (pixel.granted) window.fbq?.("consent", "revoke");
  pixel.granted = false;
  pixel.lastPageViewPath = null;
  pixel.waitingEvents.length = 0;
}

/** fbevents.js was injected in this document: its listeners outlive a revoke. */
export function metaPixelLoaded(): boolean {
  return pixel.loaded;
}

/** Same path twice in a row = Strict Mode / re-render, not a page view. */
export function trackMetaPageView(path: string): void {
  if (!pixel.granted || pixel.lastPageViewPath === path) return;
  pixel.lastPageViewPath = path;
  window.fbq?.("track", "PageView");
}

/** Once per document. Before consent it waits and goes out with the grant on this page, never without one. */
export function trackMetaEventOnce(name: MetaFunnelEvent): void {
  if (typeof window === "undefined" || pixel.sentEvents.has(name)) return;
  pixel.sentEvents.add(name);
  if (pixel.granted) window.fbq?.("track", name);
  else pixel.waitingEvents.push(name);
}
